import { useEffect } from "react";
import {
  OG_IMAGE,
  SITE_DESCRIPTION,
  SITE_KEYWORDS,
  SITE_NAME,
  SITE_TITLE,
  buildLodgingJsonLd,
} from "@/constants/seo";

function upsertMeta(
  attr: "name" | "property",
  key: string,
  content: string
) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * クライアント側で title / OGP / canonical / JSON-LD を同期する。
 * 初期 HTML の静的メタと合わせて、デプロイ先の絶対URLを補完する。
 */
export default function Seo() {
  useEffect(() => {
    const origin = window.location.origin;
    const pathname = window.location.pathname.replace(/\/$/, "") || "/";
    const basePath =
      typeof __BASE_PATH__ === "string" && __BASE_PATH__ !== "/"
        ? __BASE_PATH__.replace(/\/$/, "")
        : "";
    const canonicalPath = pathname === "/" || pathname === basePath ? "/" : pathname;
    const canonicalUrl = `${origin}${basePath}${canonicalPath === "/" ? "/" : canonicalPath}`;

    document.title = SITE_TITLE;
    document.documentElement.lang = "ja";

    upsertMeta("name", "description", SITE_DESCRIPTION);
    upsertMeta("name", "keywords", SITE_KEYWORDS);
    upsertMeta("name", "author", SITE_NAME);
    upsertMeta("name", "robots", "index, follow, max-image-preview:large");

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:locale", "ja_JP");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", SITE_TITLE);
    upsertMeta("property", "og:description", SITE_DESCRIPTION);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("property", "og:image:alt", `${SITE_NAME} — 稲取の海を望む温泉宿`);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", SITE_TITLE);
    upsertMeta("name", "twitter:description", SITE_DESCRIPTION);
    upsertMeta("name", "twitter:image", OG_IMAGE);

    upsertLink("canonical", canonicalUrl);

    const scriptId = "lodging-jsonld";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(
      buildLodgingJsonLd(`${origin}${basePath}`)
    );
  }, []);

  return null;
}
